import React from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { times } from 'lodash-es';

import TableActionsRow from './TableActionsRow';
import Skeleton from '../../../../../components/Skeleton';
import { SortableTable } from '../../../../../components/SortableTable';
import ErrorStateWithReload from '../../../../../components/ErrorStateWithReload';

import api from '../../../api';
import { columns } from '../../../configs/custom-reports-config';
import { numberToBytes } from '../../../../../utils/bytes';

const emptyTableCell = (
  <div className="exports-table-empty-state">
    <p className="text-14-150 text-secondary">
      You do not currently have any Apptopia-generated exports
    </p>
    <p className="d-flex align-items-center gap-8 text-14-150">
      <span className="text-secondary">If you need help exporting complex data, please</span>
      <a
        href="mailto:support@apptopia.com?subject=Request for Custom Reports data&body=Dear Customer Success, I am interested in getting customized data reports from Apptopia. I would like to discuss my requirements with you."
        className="text-primary text-underline"
      >
        Email Customer Success
      </a>
    </p>
  </div>
);

const skeletonRows = times(10, (n) => ({
  key: n,
  name: <Skeleton height={16} width="60%" />,
  frequency: <Skeleton height={16} width="80%" />,
  time_period: <Skeleton height={16} />,
  updated_at: <Skeleton height={16} width="70%" />,
  downloaded_at: <Skeleton height={16} width="70%" />,
  version_size: <Skeleton height={16} width="70%" />,
  actions: (
    <div className="d-flex justify-content-end gap-8">
      <Skeleton height={24} width={24} />
      <Skeleton height={24} width={24} />
      <Skeleton height={24} width={24} />
    </div>
  ),
}));

const ReportsTable = ({
  tableData,
  filters,
  settings,
  initialRowsLimit,
  hasFirstVisitAlert,
  onFiltersChange,
  onVersionsHistoryRequest,
}) => {
  const handleSort = ({ sortBy, sortOrder }) => {
    onFiltersChange({
      order_by: sortBy,
      order_dir: sortOrder,
      pagination: { ...filters.pagination, current_page: 0 },
    });
  };

  const handleOnAlertClick = (report) => {
    api.customReports[report['subscribed?'] ? 'unsubscribe' : 'subscribe'](report.id).then(() =>
      onFiltersChange(filters)
    );
  };

  const rows = tableData.map((report, i) => ({
    name: (
      <span className="d-flex">
        <span className="text-truncate">{report.name}</span>
        {['New', 'Updated'].includes(report.status) && (
          <span className="badge text-capitalize ml-xs">{report.status}</span>
        )}
      </span>
    ),
    frequency: report.frequency,
    time_period: report.time_period,
    updated_at: report.updated_at,
    downloaded_at: report.downloaded_at,
    version_size: numberToBytes(report.version_size),
    actions: (
      <TableActionsRow
        report={report}
        showPromotionTooltip={i === 0 && hasFirstVisitAlert}
        onReportAlert={() => handleOnAlertClick(report)}
        onReportTimer={() => onVersionsHistoryRequest(report.id)}
      />
    ),
  }));

  if (settings.error) return <ErrorStateWithReload />;

  return (
    <SortableTable
      tableClassName={cx(
        'table-default table-default-max-padding sticky-table-header custom-reports-table'
      )}
      defaultSortBy="created_at_and_subscribed"
      columns={columns}
      rows={settings.pending ? skeletonRows : rows}
      initialRowsLimit={initialRowsLimit}
      emptyStateText={emptyTableCell}
      hasShowMoreCell={false}
      showMoreCellColSpan={columns.length}
      onSortEnd={handleSort}
    />
  );
};

ReportsTable.propTypes = {
  tableData: T.array.isRequired,
  filters: T.object.isRequired,
  settings: T.object.isRequired,
  initialRowsLimit: T.number,
  hasFirstVisitAlert: T.bool.isRequired,
  onFiltersChange: T.func.isRequired,
  onVersionsHistoryRequest: T.func.isRequired,
};

export default ReportsTable;
